<template>
  <div v-rect-loading="loading" class="culture-container" >
    <div class="culture-middle-container">
      <div class="back-shade">
        <h1>{{ $t(`${i18nPrefix}.label.cultureBenchmark`) }}</h1>
        <p>{{ $t(`${i18nPrefix}.label.assessmentIntroOne`) }}</p>
        <p>{{ $t(`${i18nPrefix}.label.assessmentIntroTwo`) }}</p>
        <div class="culture-data">
          <div class="culture-data-item">
            <draggable
              v-model="cultureList.all"
              class="list all"
              filter=".disabled"
              :disabled="step !== 1"
              :prevent-on-filter="true"
              :group="cultureGroup"
              @add="(event) => onListAdd('all', CULTURE_TYPE_MAX.ALL, event)"
            >
              <div v-for="(item, index) in cultureList.all" :key="index">
                <div v-auto-tooltip :class="['text', {'border': item?.q_id, 'disabled': !item?.q_id}]">{{ isEn() ? item?.q_text : item?.q_cn_text }}</div>
              </div>
            </draggable>
          </div>
          <div class="culture-data-item">
            <div class="title">{{ $t(`${i18nPrefix}.label.unimportantUpperCase`) }}</div>
            <draggable
              v-model="cultureList.unimportant"
              class="list unimportant"
              filter=".disabled"
              :disabled="step !== 1"
              :prevent-on-filter="true"
              :group="unimportantGroup"
              @add="(event) => onListAdd('unimportant', CULTURE_TYPE_MAX.UNIMPORTANT, event)"
            >
              <div v-for="(item, index) in cultureList.unimportant" :key="index" @click="onChecked('unimportant', item, index)">
                <div v-auto-tooltip :class="['text', {'is-checked': item?.isChecked, 'disabled': !item?.q_id} ]">{{ isEn() ? item?.q_text : item?.q_cn_text }}</div>
              </div>
            </draggable>
          </div>
          <div class="culture-data-item">
            <div class="title">{{ $t(`${i18nPrefix}.label.neutralUpperCase`) }}</div>
            <draggable
              v-model="cultureList.neutral"
              class="list neutral"
              filter=".disabled"
              :disabled="step !== 1"
              :prevent-on-filter="true"
              :group="neutralGroup"
              @add="(event) => onListAdd('neutral', CULTURE_TYPE_MAX.NEUTRAL, event)"
            >
              <div v-for="(item, index) in cultureList.neutral" :key="index">
                <div v-auto-tooltip :class="['text', {'disabled': !item?.q_id} ]">{{ isEn() ? item?.q_text : item?.q_cn_text }}</div>
              </div>
            </draggable>
          </div>
          <div class="culture-data-item">
            <div class="title">{{ $t(`${i18nPrefix}.label.importantUpperCase`) }}</div>
            <draggable
              v-model="cultureList.important"
              class="list important"
              filter=".disabled"
              :disabled="step !== 1"
              :prevent-on-filter="true"
              :group="importantGroup"
              @add="(event) => onListAdd('important', CULTURE_TYPE_MAX.IMPORTANT, event)"
            >
              <div v-for="(item, index) in cultureList.important" :key="index" @click="onChecked('important', item, index)">
                <div v-auto-tooltip :class="['text', {'is-checked': item?.isChecked, 'disabled': !item?.q_id} ]">{{ isEn() ? item?.q_text : item?.q_cn_text }}</div>
              </div>
            </draggable>
          </div>
        </div>
        <div class="btn cancel" @click="handleCancel">{{ $t(`common.btn.cancel`) }}</div>
        <div v-show="step === 1 && isSelectComplete" class="btn next-btn" @click="onNextBtnClick">{{ $t(`common.btn.next`) }}</div>
        <div v-show="isAllChecked && step === 2" class="btn submit-btn" @click="onSubmitBtnClick">{{ $t(`common.btn.submit`) }}</div>
        <div v-show="step === 2" :class="['btn', isAllChecked ? 'back-btn' : 'back-btn-alone']" @click="backBtnClick">{{ $t(`common.btn.prev`) }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import { addI18nProperty, isEn } from '@/i18n'
import draggable from 'vuedraggable'
import { mixedRouterPush } from '@/router/routes'

const QUESTION_ANSWER = {
  MOST_UNIMPORTANT: 0,
  UNIMPORTANT: 1,
  NEUTRAL: 2,
  IMPORTANT: 3,
  MOST_IMPORTANT: 4
}
const CULTURE_TYPE_MAX = {
  ALL: 32,
  MOST_UNIMPORTANT: 3,
  UNIMPORTANT: 8,
  NEUTRAL: 16,
  IMPORTANT: 8,
  MOST_IMPORTANT: 3
}

export default {
  components: {
    draggable
  },
  data () {
    return {
      i18nPrefix: 'admin.companyCulture',
      loading: false,
      step: 1, // 页面一共两步 step in [1, 2]
      isEn,
      cultureGroup: {
        name: 'drag',
        pull: true,
        put: true
      },
      cultureList: {
        all: [],
        unimportant: [],
        neutral: [],
        important: []
      },
      QUESTION_ANSWER,
      CULTURE_TYPE_MAX
    }
  },
  computed: {
    isAllChecked () {
      const unimportantCount = _.countBy(this.cultureList.unimportant, 'isChecked').true || 0
      const importantCount = _.countBy(this.cultureList.important, 'isChecked').true || 0
      return unimportantCount === this.CULTURE_TYPE_MAX.MOST_UNIMPORTANT && importantCount === this.CULTURE_TYPE_MAX.MOST_IMPORTANT
    },
    isSelectComplete () {
      const unimportantComplete = _.every(this.cultureList.unimportant, item => item.q_id) && this.cultureList.unimportant.length === this.CULTURE_TYPE_MAX.UNIMPORTANT
      const neutralComplete = _.every(this.cultureList.neutral, item => item.q_id) && this.cultureList.neutral.length === this.CULTURE_TYPE_MAX.NEUTRAL
      const importantComplete = _.every(this.cultureList.important, item => item.q_id) && this.cultureList.important.length === this.CULTURE_TYPE_MAX.IMPORTANT
      return unimportantComplete && neutralComplete && importantComplete
    },
    unimportantGroup () {
      return {
        name: 'drag',
        pull: true,
        put: !this.isListFull('unimportant')
      }
    },
    neutralGroup () {
      return {
        name: 'drag',
        pull: true,
        put: !this.isListFull('neutral')
      }
    },
    importantGroup () {
      return {
        name: 'drag',
        pull: true,
        put: !this.isListFull('important')
      }
    },
    isRegister () {
      return this.$route.query.isRegister || false
    }
  },
  created () {
    this.initList()
  },
  methods: {
    ...mapActions('companyCulture', ['getCompanyCulture', 'updateCompanyCulture']),
    async initList () {
      this.loading = true
      try {
        const resp = await this.getCompanyCulture()
        if (!resp) {
          return
        }
        const assessJson = resp.company.assess_json
        const cultureQuestionList = _.forEach(resp.culture_question_list, item => {
          item.isChecked = false
          addI18nProperty(item, 'i18nName', 'q_cn_text', 'q_text')
        })
        this.cultureList = {
          all: [],
          unimportant: [],
          neutral: [],
          important: []
        }
        if (!assessJson) {
          this.step = 1
          this.cultureList.all = resp.culture_question_list
          this.cultureList.unimportant = _.times(this.CULTURE_TYPE_MAX.UNIMPORTANT, () => ({ q_cn_text: '', isChecked: false }))
          this.cultureList.neutral = _.times(this.CULTURE_TYPE_MAX.NEUTRAL, () => ({ q_cn_text: '', isChecked: false }))
          this.cultureList.important = _.times(this.CULTURE_TYPE_MAX.IMPORTANT, () => ({ q_cn_text: '', isChecked: false }))
        } else {
          this.step = 2
          this.cultureList.all = _.times(this.CULTURE_TYPE_MAX.ALL, () => ({ q_cn_text: '', isChecked: false }))
          _.forEach(assessJson, (value, key) => {
            const objectTemp = { ...cultureQuestionList[key - 1] }
            if ([this.QUESTION_ANSWER.MOST_UNIMPORTANT, this.QUESTION_ANSWER.MOST_IMPORTANT].includes(value)) {
              objectTemp.isChecked = true
            }
            if ([this.QUESTION_ANSWER.MOST_UNIMPORTANT, this.QUESTION_ANSWER.UNIMPORTANT].includes(value)) {
              this.cultureList.unimportant.push(objectTemp)
            } else if (value === this.QUESTION_ANSWER.NEUTRAL) {
              this.cultureList.neutral.push(objectTemp)
            } else {
              this.cultureList.important.push(objectTemp)
            }
          })
        }
      } finally {
        this.loading = false
      }
    },
    isListFull (listName) {
      return this.cultureList[listName].length > this.CULTURE_TYPE_MAX[listName.toUpperCase()]
    },
    onChecked (listName, item, index) {
      if (this.step !== 2) return
      const list = this.cultureList[listName]
      const count = _.countBy(list, 'isChecked').true || 0
      if (count >= 3 && !item.isChecked) {
        this.$message({
          message: this.$t(`${this.i18nPrefix}.message.selectBeyondTheLimit`),
          type: 'warning'
        })
      } else {
        this.$set(
          this.cultureList[listName][index],
          'isChecked',
          !this.cultureList[listName][index]?.isChecked
        )
      }
    },
    onListAdd (listName, max, event) {
      const list = this.cultureList[listName]
      const newIndex = event.newIndex
      const fromListName = event.from._prevClass.split(' ')[1]
      if (this.listRealLength(list) > max) {
        const draggedItem = list[newIndex]
        // 回滚：从目标列表中删除刚添加的元素
        list.splice(newIndex, 1)
        // 如果是从其他列表拖拽过来的，需要恢复源列表
        const oldIndex = event.oldIndex
        if (draggedItem && oldIndex >= 0) {
          // 恢复源列表
          this.cultureList[fromListName].splice(oldIndex, 0, draggedItem)
        }
        this.$message({
          message: this.$t(`${this.i18nPrefix}.message.containerAlreadyFull`),
          type: 'warning'
        })
      } else {
        const movedElement = list[newIndex]
        // 删除原来位置的元素及最后一个元素
        list.splice(newIndex, 1)
        list.pop()
        // 插入
        list.unshift(movedElement)
        this.cultureList[fromListName].push({ q_cn_text: '', isChecked: false })
      }
    },
    onNextBtnClick () {
      this.step = 2
    },
    splitByCheck (list, uncheckedVal, checkedVal) {
      const formatData = {}
      _.forEach(list, (item) => {
        formatData[item.q_id] = item.isChecked ? checkedVal : uncheckedVal
      })
      return formatData
    },
    async onSubmitBtnClick () {
      const unimportant = this.splitByCheck(this.cultureList.unimportant, this.QUESTION_ANSWER.UNIMPORTANT, this.QUESTION_ANSWER.MOST_UNIMPORTANT)
      const neutral = this.splitByCheck(this.cultureList.neutral, this.QUESTION_ANSWER.NEUTRAL, this.QUESTION_ANSWER.NEUTRAL)
      const important = this.splitByCheck(this.cultureList.important, this.QUESTION_ANSWER.IMPORTANT, this.QUESTION_ANSWER.MOST_IMPORTANT)
      const assessJson = { ...unimportant, ...neutral, ...important }
      try {
        this.loading = true
        const params = {
          assess_json: assessJson
        }
        const resp = await this.updateCompanyCulture(params)
        if (resp === 'success') {
          this.$message({
            message: this.$t(`${this.i18nPrefix}.message.cultureBenchmarkHasSuccessfully`),
            type: 'success'
          })
          this.handleCancel()
        } else {
          this.$message.error(this.$t('common.message.saveFail'))
        }
      } catch (error) {
        this.$message.error(this.$t('common.message.saveFail'))
      } finally {
        this.loading = false
      }
    },
    handleCancel () {
      const backUrl = this.$route.query.back
      if (backUrl) {
        mixedRouterPush(this.$route.query.back)
      } else {
        if (this.isRegister) {
          this.$router.push('/v-admin/job-list')
          return
        }
        this.$router.go(-1)
      }
    },
    backBtnClick () {
      const important = this.cultureList.important
      const unimportant = this.cultureList.unimportant
      _.forEach(important, (item) => {
        item.isChecked = false
      })
      _.forEach(unimportant, (item) => {
        item.isChecked = false
      })
      this.step = 1
    },
    listRealLength (list) {
      let count = 0
      if (_.every(list, item => item.q_id)) {
        count = list.length
      }
      return count
    }
  }
}
</script>

<style lang="scss" scoped>
.culture-container {
  width: 100%;
  min-height: 100%;
  background: #ffffff;
  min-width: 1320px;
  padding: 20px;
  overflow: auto;

  .culture-middle-container {
    width: 1002px;
    margin: 32px auto 0;
    color: #e1dcdc;
    background: url('https://cdn.ajinga.com/ajinga-banner/m-banner1.jpg') no-repeat center center/cover;
    .back-shade {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 20px 30px 5px;
      background-color: #000;
      opacity: 0.6;
      overflow: hidden;
    }
    .culture-data {
      display: flex;
      gap: 20px;
      justify-content: space-between;
      width: 100%;
      font-size: 14px;

      .culture-data-item:nth-child(1) {
        background-color: rgba(231, 135, 21, 0.5);
        border: 3px solid #ff8c00;
        padding: 18px 10px;

        .text {
          width: 285px;
          height: 28px;
          margin-bottom: 6px;
          cursor: pointer;
        }
      }
      .culture-data-item:not(:nth-child(1)) {
        .title {
          text-align: center;
          line-height: 50px;
          font-size: 18px;
          font-weight: bold;
          margin-bottom: 4px;
          border-top: 3px solid #ff8c00;
          background-color: rgba(231, 135, 21, 0.5);
        }

        .list {
          padding: 16px 10px;
          background-color: rgba(255, 255, 255, 0.5);

          .text {
            width: 170px;
            height: 40px;
            line-height: 36px;
            border: 2px solid #e5e5e5;
            margin-bottom: 8px;
            cursor: pointer;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            word-break: break-all;
          }
        }
      }
    }
  }
  .is-checked {
    background-color: #a44c0d;
  }
  .btn {
    position: absolute;
    bottom: 20px;
    right: 80px;
    width: 120px;
    height: 40px;
    line-height: 40px;
    text-align: center;
    font-size: 18px;
    color: #e1dcdc;
    border: 2px solid  #e5e5e5;
    background-color: rgba(220, 220, 220, 0.5);
    cursor: pointer;
  }
  .cancel {
    right: 520px;
  }
  .next-btn, .submit-btn {
    right: 30px;
  }
  .back-btn {
    right: 180px;
  }
  .back-btn-alone {
    right: 30px;
  }
  .border {
    border: 2px solid #e5e5e5;
  }
  .footer {
    align-items: center;
  }
}
</style>
